import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { tracksApi } from '../api/tracks';

export function useLikes(trackIds: string[]) {
  const [likedTracks, setLikedTracks] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Fetch liked status when trackIds change
  useEffect(() => {
    if (!isAuthenticated || trackIds.length === 0) {
      setLikedTracks(new Set());
      return;
    }

    const fetchLikedStatus = async () => {
      setIsLoading(true);
      try {
        const result = await tracksApi.checkLiked(trackIds);
        const likedSet = new Set<string>();
        for (const [id, isLiked] of Object.entries(result)) {
          if (isLiked) {
            likedSet.add(id);
          }
        }
        setLikedTracks(likedSet);
      } catch (error) {
        console.error('Failed to fetch liked status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLikedStatus();
  }, [trackIds.join(','), isAuthenticated]);

  const toggleLike = useCallback(async (trackId: string): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }

    const wasLiked = likedTracks.has(trackId);

    // Optimistic update
    setLikedTracks((prev) => {
      const newSet = new Set(prev);
      if (wasLiked) {
        newSet.delete(trackId);
      } else {
        newSet.add(trackId);
      }
      return newSet;
    });

    try {
      if (wasLiked) {
        await tracksApi.unlike(trackId);
      } else {
        await tracksApi.like(trackId);
      }
      return true;
    } catch (error) {
      // Revert optimistic update on error
      setLikedTracks((prev) => {
        const newSet = new Set(prev);
        if (wasLiked) {
          newSet.add(trackId);
        } else {
          newSet.delete(trackId);
        }
        return newSet;
      });
      console.error('Failed to toggle like:', error);
      return false;
    }
  }, [isAuthenticated, likedTracks]);

  const isLiked = useCallback((trackId: string) => likedTracks.has(trackId), [likedTracks]);

  return {
    likedTracks,
    isLoading,
    toggleLike,
    isLiked,
    isAuthenticated,
  };
}
